// Sheet — boards ToileMobileTapped / ToileMobileWorking, verbatim values.

// `touch-action: none`: a touch that begins on the layer never becomes the browser's own pan, so the
// wall behind the scrim holds still and the fixed sheet stops jittering against a moving layout
// viewport (operator-observed on iPhone 16, 2026-09-02). Neither `overscroll-behavior` (scroll
// containers only) nor `body { overflow: hidden }` (ignored by iOS while the visual viewport is
// smaller than the layout one — i.e. with the keyboard up) can do this job. Taps, long-press
// selection and the field's focus are untouched; what a drag means is the wall's return listener's
// call.
// z-index: `--tl-z-arena` (products/toile/styles/index.scss — the wall's stacking order, one source).
.arena {
    position: fixed;
    inset: 0;
    z-index: var(--tl-z-arena);
    touch-action: none;
}

.scrim {
    position: absolute;
    inset: 0;
    background: var(--tl-scrim);
}

.esc {
    position: absolute;
    top: 14px;
    left: 14px;
    z-index: 3;
    display: flex;
    align-items: center;
    gap: 8px;
    background: color-mix(in srgb, var(--tl-surface) 92%, transparent);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-pill);
    padding: 7px 13px;
    font-size: 12.5px;
    color: var(--tl-muted);
    font-family: var(--tl-font);
}

@keyframes sheetRise {
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
}

.sheet {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 6;
    background: color-mix(in srgb, var(--tl-surface) 97%, transparent);
    border-top: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-container-lg) var(--tl-radius-container-lg) 0 0;
    padding: 10px 14px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    box-shadow: 0 -20px 60px color-mix(in srgb, var(--tl-shadow-ink) 62%, transparent);
    animation: sheetRise 220ms ease-out;
}

.grabber {
    width: 40px;
    height: 4px;
    border-radius: var(--tl-radius-pill);
    background: var(--tl-border-strong);
    padding: 12px 0; // enlarges the swipe-catch area without changing the visible bar
    background-clip: content-box;
}

// Width and height set inline by the box fit, on the sheet's own box (`frame.sheetFrameBox`) —
// see `Column.module.scss`'s own `.result`.
.result {
    border-radius: var(--tl-radius-art);
    overflow: hidden;
    background: var(--tl-stage);
    position: relative;
    // The cell at work (`WorkingCell.module.scss`, rendered inside while `working`) sizes its
    // place numeral off the box it stands in via `cqw` — see `Column.module.scss`'s own `.result`.
    container-type: inline-size;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

.resultScratch {
    border: 1px dashed color-mix(in srgb, var(--tl-accent) 45%, transparent);
    background: color-mix(in srgb, var(--tl-accent) 4%, transparent);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 18px;
    text-align: center;
    color: var(--tl-accent);

    span {
        font-family: var(--tl-font);
        font-size: 14px;
        line-height: 1.3;
    }
}

// The cap — the armed chain's words, centred (board `WordsAsDoors`: doors alone, no thumbnail, no
// "From:"; see `Column.module.scss`'s own `.caption` for the full rationale). Doors when the chain
// has any (`WordDoors`); `.plain` — same face and size — when it has none.
// `max-width: 100%` — unlike Column's own caption (`styles.caption`, passed straight into
// `WordDoors`' `className`), this wrapper carries no width bound of its own; as a centred flex
// child of `.sheet` (align-items: center, not stretch) it sizes to its own content by default, so
// a long typed direction's chip row could grow past the sheet's own edge instead of wrapping/
// truncating within it. `.sheet`'s `left: 0; right: 0` gives it a definite width for this
// percentage to resolve against. The row inside (`WordDoors`' own `.row`) already carries
// `min-width: 0`, and a flex item's own default `flex-shrink` is 1, so once this wrapper is capped
// the row shrinks to fit it and `.chip`'s own `max-width: 100%` clamp (second-look fix) can engage.
.cap {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 6px;
    max-width: 100%;
}

.plain {
    font-family: var(--tl-font-label);
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--tl-muted);
    // As a <Link> (a chip-less tile with a pack page): the same dress, brightening on hover.
    text-decoration: none;
    transition: color 150ms ease;

    &:is(a):hover,
    &:is(a):focus-visible {
        color: var(--tl-text);
    }
}

// The unfolded ratio row, rising from the bar (board frame 4). Centred across the sheet rather
// than aligned under the chip: at 390 the six pills are nearly the whole width, so the sheet's own
// edges are the alignment.
.ratioRow {
    width: 100%;
    justify-content: center;
}

// The terms gate's card — same slot as `.ratioRow`, one fold at a time.
.consentSlot {
    width: 100%;
}

.bar {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    background: var(--tl-stage);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-container);
    padding: 8px 8px 8px 16px;
}

.slot {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
}

// See `Column.module.scss`'s own comments for `.idChip`/`.pencil` — same shapes, the sheet's own
// bar.
.idChip {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
}

// The bar's OPEN identity slot (plan `2026_09_18_armed_frame_budget.md` § D6) — see
// `Column.module.scss`'s own comment; same shape, the sheet's own bar.
.idOpen {
    flex: 1;
    min-width: 0;
    display: flex;
}

.pencil {
    // 44px, not the column's 40 — the sheet is the phone surface (`RatioFold.module.scss`'s own
    // `[data-wall-surface='sheet']` rule states the same 44px CSS px floor for a tap target).
    flex: none;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    // `--tl-door*` — the house's own door colour, the same dress as `Column.module.scss`'s pencil.
    border: 1px solid var(--tl-door-border);
    border-radius: var(--tl-radius-chrome);
    background: var(--tl-door-bg);
    color: var(--tl-door);
    cursor: pointer;

    &:focus-visible {
        outline: 1px solid var(--tl-accent);
        outline-offset: 2px;
    }
}

// One caret, the browser's own — see `Column.module.scss`'s `.input` (N15).
.input {
    flex: 1;
    min-width: 0;
    background: none;
    border: none;
    outline: none;
    caret-color: var(--tl-accent);
    font-family: var(--tl-font);
    // 16px, not the bar's 14.5: iOS Safari zooms the page into a focused form control set smaller
    // than this, and the zoom outlives the blur (iOS ≥ 10 ignores `maximum-scale` — WebKit blog
    // 7367 — so the size is the only lever).
    font-size: 16px;
    color: var(--tl-text);

    &::placeholder {
        color: var(--tl-dim);
    }
}

.verb {
    background: var(--tl-accent);
    color: var(--tl-accent-ink);
    font-family: var(--tl-font);
    font-weight: 500;
    font-size: 13.5px;
    padding: 12px 18px;
    border: none;
    border-radius: var(--tl-radius-chrome);
    white-space: nowrap;
    box-shadow: 0 0 24px color-mix(in srgb, var(--tl-accent) 35%, transparent);
}

// The line (BRIEF §11's vocabulary) while the column is open — an error that did not land, or the
// state of the chain being worked on. Colour and case are the Notice component's job now
// (`@core/ui/Notice`) — this class carries position/type only.
.note {
    margin: 10px 0 0;
    font-family: var(--tl-font-label);
    font-size: 10px;
    letter-spacing: 0.16em;
    text-align: center;
}

// The cell at work's own reduced-motion rule lives with the rest of it, in
// `WorkingCell.module.scss` — nothing animates in this file any more (the cap's own discreet ↗
// carries no nudge, board A trimmed 2026-09-18).
